import { isSelectableSlot } from "@/helpers/meetingSlots";
import Modal from "@/components/atoms/Modal";
import Select, { SelectPaginateAsync } from "@/components/atoms/Select";
import CommercialBookingFields, {
  CommercialBookingSelection,
} from "@/components/molecules/calendar/CommercialBookingFields";
import CsmAvailability from "@/components/molecules/calendar/CsmAvailability";
import CompanyOption from "@/components/molecules/modals/company/CompanyOption";
import MontageDiagnosticChecklist from "@/components/molecules/preformatif-diagnostic/MontageDiagnosticChecklist";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import useFrenchHolidays from "@/hooks/useFrenchHolidays";
import useLoadOptions from "@/hooks/useLoadOptions";
import Company from "@/interfaces/Company";
import { CompanyCourseFilters } from "@/interfaces/CompanyCourse";
import { IFilters } from "@/interfaces/Fetch";
import User from "@/interfaces/IUser";
import {
  CALENDAR_TYPE_OPTIONS,
  CalendarType,
  COMPANY_MEETING_TYPE,
  MeetingForm,
} from "@/interfaces/Meeting";
import { IModalMethods } from "@/interfaces/Modal";
import { useGetCompanyQuery } from "@/services/companies";
import { useStoreCompanyMeetingsMutation } from "@/services/companies/meetings";
import { useStoreMeetingMutation } from "@/services/meetings";
import { useGetMontageAvailabilityQuery } from "@/services/montageMeetings";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { DateTime } from "luxon";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Col, Form, Modal as RBModal, Row } from "react-bootstrap";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { ToastContentProps, toast } from "react-toastify";

export const TITLE_REQUIRED_MESSAGE =
  "Le titre est obligatoire quand aucune entreprise n'est choisie.";
export const COMPANY_REQUIRED_MESSAGE =
  "En montage, un commercial rattache toujours le rendez-vous à une de ses entreprises.";
export const DIAGNOSTIC_REQUIRED_MESSAGE =
  "Choisissez la formation concernée : un rendez-vous montage exige son diagnostic préformatif finalisé.";

export interface IStoreMeetingModalMethods extends IModalMethods {
  /** Ouvre la modale sur le créneau cliqué dans un calendrier (vue semaine ou
   *  jour), date et heure déjà remplies. */
  openAt: (start: Date) => void;
}

interface IProps {
  /** Fiche entreprise : l'entreprise est imposée. Absente depuis un calendrier,
   *  où elle se choisit dans le formulaire et reste facultative (spec 2026-10-08). */
  companyId?: string;
  /** Calendrier d'où l'on crée : pré-remplit le champ Calendrier et le verrouille. */
  calendarType?: CalendarType;
}

const StoreCompanyMeetingModal: ForwardRefRenderFunction<
  IStoreMeetingModalMethods,
  IProps
> = ({ companyId: fixedCompanyId, calendarType: lockedCalendarType }, ref) => {
  const user = useSelector(selectCurrentUser);
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const pickCompany = !fixedCompanyId;
  const lockedCalendarOption = CALENDAR_TYPE_OPTIONS.find(
    (option) => option.value === lockedCalendarType,
  );

  const {
    control,
    register,
    setValue,
    setError,
    clearErrors,
    reset,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<MeetingForm>({
    defaultValues: { calendarType: lockedCalendarOption },
  });

  const pickedCompany = watch("company");
  const companyId = fixedCompanyId ?? pickedCompany?.id;
  // `currentData`, pas `data` : RTK Query garde la dernière fiche tant que la
  // requête est sautée, si bien que rechoisir la même entreprise ne changeait
  // rien et laissait le statut vide (un télépro, sans champ Statut, recevait
  // alors un 422). `currentData` retombe à `undefined` entre deux choix.
  const { currentData: company } = useGetCompanyQuery(companyId ?? "", {
    skip: !companyId,
  });

  const isMontage = watch("calendarType")?.value === "montage";
  const isCommercialBooking = !isMontage && !!companyId;
  const compactCommercialFields =
    isCommercialBooking && checkIfUserHasRoles(user, ["ADMIN", "CSM", "AE"]);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [initialCommercialStart, setInitialCommercialStart] =
    useState<string>();
  const [bookingRefresh, setBookingRefresh] = useState(0);
  const [bookingConflict, setBookingConflict] = useState("");
  const [booking, setBooking] = useState<
    CommercialBookingSelection & { companyId?: string }
  >({ startAt: "", ready: false });
  useEffect(() => {
    setBooking({ startAt: "", ready: false });
    setInitialCommercialStart(undefined);
    setBookingConflict("");
  }, [isMontage]);
  const onBookingChange = useCallback(
    (selection: CommercialBookingSelection) => {
      setBooking({ ...selection, companyId });
      setValue("startAt", selection.startAt);
      if (selection.ready) clearErrors("startAt");
    },
    [companyId, setValue, clearErrors],
  );
  // CSM == ADMIN dans checkIfUserHasRoles : on teste le rôle lui-même, sinon
  // un ADMIN se présélectionnerait sans figurer parmi les CSM (spec §2.3).
  const isCsm = !!user?.roles?.includes("CSM");
  // Un commercial n'agit que sur les montages de sa fiche (ADR 0007) : depuis
  // un calendrier, l'entreprise lui devient obligatoire en montage.
  const companyRequired =
    pickCompany && isMontage && !checkIfUserHasRoles(user, ["ADMIN", "CSM"]);
  // Un montage met en place une formation : un commercial ne le pose que sur
  // des formations au diagnostic préformatif finalisé ; ADMIN et CSM passent
  // outre (ADR 0012). L'API vérifie le statut de chaque diagnostic.
  const diagnosticRequired =
    isMontage && !checkIfUserHasRoles(user, ["ADMIN", "CSM"]);
  const linkedCourses = watch("companyCourses") ?? [];

  // Un rendez-vous montage occupe un créneau d'UNE heure et appartient
  // obligatoirement à un CSM : on choisit un jour, un CSM, puis un créneau —
  // jamais une heure libre. Un CSM est son propre CSM par défaut.
  const [montageDate, setMontageDate] = useState("");
  const [montageCsm, setMontageCsm] = useState<User | null>(
    isCsm ? user : null,
  );
  const [montageHour, setMontageHour] = useState<string | null>(null);
  const canOverbook = checkIfUserHasRoles(user, ["ADMIN", "CSM"]);
  // Même requête que la grille du CSM, servie par le même cache : elle dit si
  // le créneau retenu reste libre quand on change de CSM.
  const { currentData: montageAvailability } = useGetMontageAvailabilityQuery(
    { date: montageDate },
    { skip: !isMontage || !montageDate },
  );

  useEffect(() => {
    // Sans jour, la grille ne peut rien montrer : on part d'aujourd'hui plutôt
    // que d'un écran vide qui n'indique pas ce qui manque.
    if (isMontage && !montageDate) {
      setMontageDate(DateTime.now().toFormat("yyyy-MM-dd"));
    }
  }, [isMontage, montageDate]);

  useEffect(() => {
    if (!isMontage) return;
    setValue(
      "startAt",
      montageDate && montageHour ? `${montageDate}T${montageHour}` : "",
    );
  }, [isMontage, montageDate, montageHour, setValue]);

  const {
    loadCompanyStatusesOptions,
    loadCompanyCoursesOptions,
    loadUsersOptions,
    loadCompaniesOptions,
  } = useLoadOptions();

  // Les formations suivent l'entreprise retenue : en changer recharge la liste,
  // d'où aussi le `cacheUniqs` du select.
  const courseAdditional = useMemo<CompanyCourseFilters>(
    () => ({ pageIndex: 0, companyId }),
    [companyId],
  );

  const [storeCompanyMeetingAsync, { isLoading: isStoringCompanyMeeting }] =
    useStoreCompanyMeetingsMutation();
  const [storeMeetingAsync, { isLoading: isStoringMeeting }] =
    useStoreMeetingMutation();

  const defaultAdditional = useRef<IFilters>({
    pageIndex: 0,
  }).current;

  const currentYear = DateTime.now().year;
  const holidayYears = useMemo(
    () => [currentYear, currentYear + 1],
    [currentYear],
  );
  const { getHolidayForDate } = useFrenchHolidays(holidayYears);

  const [holidayWarning, setHolidayWarning] = useState<{
    pendingData: MeetingForm;
    proposalToken?: string;
    holidayName: string;
    formattedDate: string;
  } | null>(null);

  const showOrHiddenModal = () => {
    setBookingOpen((value) => !value);
    ModalRef.current?.showOrHiddenModal();
  };

  const openAt = (start: Date) => {
    const slot = DateTime.fromJSDate(start);
    setValue("startAt", slot.toFormat("yyyy-MM-dd'T'HH:mm"));
    setInitialCommercialStart(slot.toFormat("yyyy-MM-dd'T'HH:mm"));
    setMontageDate(slot.toFormat("yyyy-MM-dd"));
    // Un montage occupe un créneau d'une heure de la grille du CSM : 10:30
    // retient 10:00, et une heure hors de la grille ne retient que le jour.
    const hour = slot.toFormat("HH:mm");
    setMontageHour(hour);
    showOrHiddenModal();
  };

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
    openAt,
  }));

  const statusCompany = useRef<string>();
  useEffect(() => {
    // Initialize once per company choice. A proposal conflict refetches the
    // company too, but must preserve the status already chosen in this form.
    if (company?.status && statusCompany.current !== companyId) {
      setValue("status", company.status);
      statusCompany.current = companyId;
    }
  }, [company, companyId, setValue]);

  // Le formulaire survit à la fermeture de la modale : sans remise à zéro,
  // l'entreprise et le titre du rendez-vous précédent réapparaîtraient.
  const resetForm = () => {
    setBookingOpen(false);
    setBooking({ startAt: "", ready: false });
    setInitialCommercialStart(undefined);
    setBookingConflict("");
    setHolidayWarning(null);
    reset({
      calendarType: lockedCalendarOption,
      // Explicite : sans valeur, react-hook-form relirait l'heure restée dans
      // le champ si la modale se rouvre avant d'avoir démonté son contenu.
      startAt: "",
      // Depuis la fiche, le statut de l'entreprise reste pré-rempli.
      status: fixedCompanyId ? company?.status : undefined,
    });
    setMontageDate("");
    setMontageHour(null);
    setMontageCsm(isCsm ? user : null);
  };

  const onCompanyChange = (
    value: Company | null,
    onChange: (value: Company | null) => void,
  ) => {
    // Une autre entreprise a d'autres formations et un autre statut : on vide ;
    // le statut se re-pré-remplit depuis la nouvelle fiche.
    if (value?.id !== pickedCompany?.id) {
      statusCompany.current = undefined;
      setBooking({ startAt: "", ready: false });
      // A first company choice can validate the original calendar click.
      // Replacing/removing a company always discards its previous hour.
      if (pickedCompany) setInitialCommercialStart(undefined);
      setValue("startAt", "");
      setBookingConflict("");
      setValue("companyCourses", []);
      setValue("status", null);
    }
    onChange(value);
  };

  const submitMeeting = (formData: MeetingForm, proposalToken?: string) => {
    const { company, title, ...companyForm } = formData;
    const targetCompanyId = fixedCompanyId ?? company?.id;
    const common = {
      type: formData.type?.value ?? COMPANY_MEETING_TYPE[0].value,
      calendarType:
        formData.calendarType?.value ?? CALENDAR_TYPE_OPTIONS[0].value,
      ...(isMontage && montageCsm ? { csmOwnerId: montageCsm.id } : {}),
      ...(!isMontage && targetCompanyId
        ? { bookingProtocol: 2 as const, proposalToken }
        : {}),
    };

    // Avec une entreprise, le chemin de toujours ; sans, POST /meetings
    // (spec 2026-10-08, approche A).
    const request = targetCompanyId
      ? storeCompanyMeetingAsync([
          targetCompanyId,
          {
            ...companyForm,
            ...common,
            statusId: formData.status?.id,
            companyCourseIds: formData.companyCourses?.map((c) => c.id) ?? [],
          },
        ]).unwrap()
      : storeMeetingAsync({
          ...common,
          startAt: formData.startAt,
          title: (title ?? "").trim(),
        }).unwrap();

    const handledRequest = request.catch((error: FetchBaseQueryError) => {
      const data = error.data as
        | { code?: string; message?: string; companyCourseIds?: string[] }
        | undefined;
      if (isMontage && error.status === 400 && data?.companyCourseIds) {
        setError("companyCourses", { type: "server", message: data.message });
      }
      if (isCommercialBooking && error.status === 409) {
        setBooking({ startAt: "", ready: false });
        setValue("startAt", "");
        setInitialCommercialStart(undefined);
        setBookingConflict(
          data?.message ??
            "La proposition commerciale a changé. Choisissez à nouveau une heure.",
        );
        setBookingRefresh((value) => value + 1);
      }
      throw error;
    });
    toast.promise(handledRequest, {
      pending: "Ajout du rendez-vous en cours...",
      success: {
        render() {
          showOrHiddenModal();
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              Le rendez-vous a bien été ajouté 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          const message = (data?.data as { message?: string } | undefined)
            ?.message;
          if ((data?.status === 400 || data?.status === 409) && message)
            return message;
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }
          if (data?.status === 403) {
            return "Vous n'avez pas le droit de créer ce rendez-vous.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  const onSubmit: SubmitHandler<MeetingForm> = (formData) => {
    // Ces deux règles dépendent de l'entreprise choisie : vérifiées ici plutôt
    // que par des `rules` figées au premier rendu.
    if (companyRequired && !formData.company) {
      setError("company", {
        type: "required",
        message: COMPANY_REQUIRED_MESSAGE,
      });
      toast.error(INVALID_FORM_MESSAGE);
      return;
    }
    if (
      diagnosticRequired &&
      (fixedCompanyId ?? formData.company) &&
      !formData.companyCourses?.length
    ) {
      setError("companyCourses", {
        type: "required",
        message: DIAGNOSTIC_REQUIRED_MESSAGE,
      });
      toast.error(INVALID_FORM_MESSAGE);
      return;
    }
    if (!(fixedCompanyId ?? formData.company) && !formData.title?.trim()) {
      setError("title", { type: "required", message: TITLE_REQUIRED_MESSAGE });
      toast.error(INVALID_FORM_MESSAGE);
      return;
    }

    if (
      isCommercialBooking &&
      (!booking.ready ||
        !company ||
        booking.companyId !== companyId ||
        booking.startAt !== formData.startAt)
    ) {
      setError("startAt", {
        type: "availability",
        message: "Choisissez un créneau commercial disponible.",
      });
      toast.error(INVALID_FORM_MESSAGE);
      return;
    }

    const matchedHoliday = formData.startAt
      ? getHolidayForDate(formData.startAt)
      : undefined;
    if (matchedHoliday) {
      setHolidayWarning({
        pendingData: formData,
        proposalToken: isCommercialBooking ? booking.proposalToken : undefined,
        holidayName: matchedHoliday.name,
        formattedDate: DateTime.fromISO(formData.startAt)
          .setLocale("fr")
          .toFormat("d MMMM yyyy"),
      });
      return;
    }

    submitMeeting(
      formData,
      isCommercialBooking ? booking.proposalToken : undefined,
    );
  };

  const onConfirmHoliday = () => {
    if (!holidayWarning) return;
    const pending = holidayWarning.pendingData;
    const pendingToken = holidayWarning.proposalToken;
    setHolidayWarning(null);
    submitMeeting(pending, pendingToken);
  };

  const onError = () => toast.error(INVALID_FORM_MESSAGE);

  return (
    <>
      <Modal
        ref={ModalRef}
        title="Ajouter un rendez-vous"
        isLoading={
          isStoringCompanyMeeting ||
          isStoringMeeting ||
          (isCommercialBooking &&
            (!booking.ready || !company || booking.companyId !== companyId))
        }
        requiredLabel
        onHide={resetForm}
        onSubmit={handleSubmit(onSubmit, onError)}
      >
        <Row className="gx-3">
          {fixedCompanyId && (
            <Col lg={12}>
              <Form.Group className="mb-3" controlId="meeting-company">
                <Form.Label>Entreprise</Form.Label>
                <Form.Control
                  type="text"
                  value={company?.name ?? ""}
                  placeholder="Chargement de l'entreprise..."
                  readOnly
                />
                <Form.Text>
                  Le rendez-vous est rattaché à cette fiche entreprise.
                </Form.Text>
              </Form.Group>
            </Col>
          )}
          {pickCompany && (
            <Col lg={12}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Entreprise{" "}
                  {companyRequired ? (
                    <span className="text-danger">*</span>
                  ) : (
                    <span className="fw-normal text-600">(facultatif)</span>
                  )}
                </Form.Label>
                <Controller
                  control={control}
                  name="company"
                  render={({ field }) => (
                    <SelectPaginateAsync
                      loadOptions={loadCompaniesOptions}
                      additional={defaultAdditional}
                      debounceTimeout={300}
                      value={field.value ?? null}
                      getOptionLabel={(option: Company) => option.name}
                      getOptionValue={(option: Company) => option.id}
                      // La recherche porte sur tous les champs usuels : l'option
                      // montre de quoi reconnaître la bonne fiche.
                      formatOptionLabel={CompanyOption}
                      onChange={(value) =>
                        onCompanyChange(value as Company | null, field.onChange)
                      }
                      isClearable
                      placeholder="Rechercher par nom, SIRET, ville, téléphone..."
                      noOptionsMessage={() =>
                        "Aucune entreprise ne correspond. Laissez vide pour un rendez-vous sans entreprise."
                      }
                    />
                  )}
                />
                {errors.company ? (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {errors.company.message}
                  </Form.Control.Feedback>
                ) : (
                  !pickedCompany && (
                    <Form.Text>
                      {companyRequired
                        ? "En montage, choisissez une de vos entreprises."
                        : "Sans entreprise, donnez un titre au rendez-vous."}
                    </Form.Text>
                  )
                )}
              </Form.Group>
            </Col>
          )}
          {/* Un commercial en montage n'a pas le choix du titre : l'entreprise
              lui est obligatoire (ADR 0007). */}
          {pickCompany && !pickedCompany && !companyRequired && (
            <Col lg={6}>
              <Form.Group className="mb-3" controlId="meeting-title">
                <Form.Label>
                  Titre <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  maxLength={255}
                  placeholder="Ex. Point équipe, Salon Formation Lyon"
                  {...register("title", { shouldUnregister: true })}
                  isInvalid={!!errors.title}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.title?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          )}
          {companyId && checkIfUserHasRoles(user, ["ADMIN", "AE", "CSM"]) && (
            <Col lg={compactCommercialFields ? 4 : 6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Statut <span className="text-danger">*</span> :
                </Form.Label>
                {/* Pas de shouldUnregister : la modale démonte son contenu à la
                    fermeture, et le statut pré-rempli de la fiche disparaissait
                    pour l'ouverture suivante. */}
                <Controller
                  control={control}
                  name="status"
                  rules={{ required: "Le statut est obligatoire" }}
                  render={({ field }) => (
                    <SelectPaginateAsync
                      loadOptions={loadCompanyStatusesOptions}
                      value={field.value}
                      defaultOptions
                      additional={defaultAdditional}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id.toString()}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Form.Group>
            </Col>
          )}
          <Col lg={compactCommercialFields ? 4 : 6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Type de rendez-vous <span className="text-danger">*</span>
              </Form.Label>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <Select
                    options={COMPANY_MEETING_TYPE}
                    defaultValue={COMPANY_MEETING_TYPE[0]}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Form.Group>
          </Col>
          {checkIfUserHasRoles(user, ["ADMIN", "CSM", "AE"]) && (
            <Col lg={compactCommercialFields ? 4 : 6}>
              <Form.Group className="mb-3">
                <Form.Label>Calendrier</Form.Label>
                <Controller
                  control={control}
                  name="calendarType"
                  render={({ field }) => (
                    <Select
                      options={CALENDAR_TYPE_OPTIONS}
                      defaultValue={CALENDAR_TYPE_OPTIONS[0]}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      value={field.value}
                      onChange={field.onChange}
                      // Ouverte depuis un calendrier, le rendez-vous atterrit
                      // dans ce calendrier-là (spec 2026-10-08, §2.2).
                      isDisabled={!!lockedCalendarType}
                    />
                  )}
                />
              </Form.Group>
            </Col>
          )}
          {!isMontage && !companyId && (
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Date et heure <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  {...register(`startAt`, {
                    validate: (value) => {
                      if (DateTime.fromISO(value) < DateTime.now()) {
                        return "La date de début doit être supérieure à la date actuelle";
                      }
                    },
                    required: "La date de début est obligatoire",
                  })}
                  isInvalid={!!errors.startAt}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.startAt?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          )}
          {isCommercialBooking && bookingOpen && companyId && (
            <Col lg={12}>
              {bookingConflict && (
                <p role="alert" className="text-warning">
                  {bookingConflict}
                </p>
              )}
              <CommercialBookingFields
                companyId={companyId}
                initialStartAt={initialCommercialStart}
                refreshKey={bookingRefresh}
                onChange={onBookingChange}
              />
              {errors.startAt && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.startAt.message}
                </Form.Control.Feedback>
              )}
            </Col>
          )}

          {isMontage && (
            <>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Jour <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    aria-label="Jour du rendez-vous"
                    value={montageDate}
                    onChange={(event) => {
                      setMontageDate(event.target.value);
                      // Changer de jour invalide le créneau : les occupations
                      // affichées ne sont plus celles qu'on avait sous les yeux.
                      setMontageHour(null);
                    }}
                  />
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    CSM <span className="text-danger">*</span>
                  </Form.Label>
                  <SelectPaginateAsync
                    loadOptions={loadUsersOptions}
                    additional={{ pageIndex: 0, roles: ["CSM"] }}
                    getOptionLabel={(option: User) =>
                      `${option.firstname} ${option.lastname}`
                    }
                    getOptionValue={(option: User) => option.id.toString()}
                    value={montageCsm}
                    onChange={(value) => {
                      const csm = value as User | null;
                      setMontageCsm(csm);
                      // Le créneau retenu (souvent cliqué dans le calendrier)
                      // survit au choix du CSM s'il est libre ET dans ses
                      // disponibilités ; pris, hors horaires ou inconnu, on le
                      // retire plutôt que de poser un créneau qu'on n'a pas
                      // vu valable chez lui.
                      const entry = csm
                        ? montageAvailability?.find((e) => e.csmId === csm.id)
                        : undefined;
                      const keep =
                        !!csm &&
                        !!montageAvailability &&
                        !!montageHour &&
                        isSelectableSlot(entry, montageHour, {
                          allowBusy: canOverbook,
                          allowUnavailable: canOverbook,
                        });
                      if (!keep) setMontageHour(null);
                    }}
                    placeholder="Sélectionner un CSM..."
                  />
                </Form.Group>
              </Col>
              <Col lg={12}>
                <p className="fs--1 mb-2 text-700">
                  {/* Le créneau cliqué dans le calendrier est retenu avant même
                      le choix du CSM : on l'annonce, sinon seul le jour
                      paraît rempli. */}
                  {!montageCsm
                    ? montageHour
                      ? `Créneau retenu : ${montageHour}. Sélectionnez un CSM pour vérifier qu'il est libre.`
                      : "Sélectionnez un CSM pour voir ses créneaux disponibles."
                    : montageHour
                      ? `Créneau retenu : ${montageHour}.`
                      : canOverbook
                        ? "Cliquez sur un créneau pour choisir l'heure. Vous pouvez surcharger un créneau déjà pris."
                        : "Cliquez sur un créneau vert pour choisir l'heure du rendez-vous."}
                </p>
                {canOverbook && (
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="new-montage-time">
                      Heure du rendez-vous
                    </Form.Label>
                    <Form.Control
                      id="new-montage-time"
                      type="time"
                      step={60}
                      value={montageHour ?? ""}
                      onChange={(e) => setMontageHour(e.target.value || null)}
                    />
                  </Form.Group>
                )}
                {montageCsm && montageDate && (
                  <div className="mb-3">
                    <CsmAvailability
                      date={montageDate}
                      csmId={montageCsm.id}
                      onSelect={setMontageHour}
                      selectedHour={montageHour}
                      onInvalidSelection={() => setMontageHour(null)}
                      allowBusy={canOverbook}
                      allowUnavailable={canOverbook}
                    />
                  </div>
                )}
              </Col>
            </>
          )}

          {companyId && (
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Formations liées{" "}
                  {diagnosticRequired ? (
                    <span className="text-danger">*</span>
                  ) : (
                    <span className="fw-normal text-600">(optionnel)</span>
                  )}
                </Form.Label>
                <Controller
                  control={control}
                  name="companyCourses"
                  shouldUnregister
                  render={({ field }) => (
                    <SelectPaginateAsync
                      loadOptions={loadCompanyCoursesOptions}
                      additional={courseAdditional}
                      cacheUniqs={[companyId]}
                      getOptionLabel={(option) =>
                        option.course?.name ?? "Formation"
                      }
                      getOptionValue={(option) => option.id.toString()}
                      value={field.value}
                      onChange={(value) => {
                        clearErrors("companyCourses");
                        field.onChange(value);
                      }}
                      isClearable
                      isMulti
                    />
                  )}
                />
                {errors.companyCourses ? (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {errors.companyCourses.message}
                  </Form.Control.Feedback>
                ) : (
                  diagnosticRequired &&
                  !linkedCourses.length && (
                    <Form.Text>
                      Choisissez la formation concernée : son diagnostic
                      préformatif doit être finalisé.
                    </Form.Text>
                  )
                )}
                {isMontage && (
                  <MontageDiagnosticChecklist
                    companyId={companyId}
                    courses={linkedCourses}
                  />
                )}
              </Form.Group>
            </Col>
          )}
        </Row>
      </Modal>
      <RBModal
        show={!!holidayWarning}
        onHide={() => setHolidayWarning(null)}
        centered
      >
        <RBModal.Header closeButton>
          <RBModal.Title className="text-warning">
            Attention, jour férié
          </RBModal.Title>
        </RBModal.Header>
        <RBModal.Body>
          {holidayWarning && (
            <p className="mb-0">
              Le <strong>{holidayWarning.formattedDate}</strong> est un jour
              férié ({holidayWarning.holidayName}). Êtes-vous sûr de vouloir
              planifier un rendez-vous ce jour-là ?
            </p>
          )}
        </RBModal.Body>
        <RBModal.Footer>
          <Button variant="secondary" onClick={() => setHolidayWarning(null)}>
            Annuler
          </Button>
          <Button variant="warning" onClick={onConfirmHoliday}>
            Confirmer
          </Button>
        </RBModal.Footer>
      </RBModal>
    </>
  );
};

export default forwardRef(StoreCompanyMeetingModal);
